import produce from 'immer';
import { add } from 'date-fns';
import {
  OrderInterface,
  PromotionInterface,
  ReasonInterface,
  SubsidiaryStockRestrictionsInterface
} from '@e3labs/isa-data-dictionary';

import {
  OrderCreateInterface,
  OrderItemCreateInterface,
  ProductModalProps,
  SubsidiaryStockRestrictions
} from './order-interface';
import { StateListType } from '~/types/state-list-type';
import { ModalType } from '~/types/modal-types';
import { IReduxAction } from '~/services/interfaces/common-interface';
import {
  SET_LOADING_DATA,
  SET_LOADING_PRODUCT_DATA,
  SET_LOADING_SEND,
  SET_LOADING_SELECTS,
  GET_ALL_SUCCESS,
  GET_BY_ID_SUCCESS,
  SET_PAGE,
  SET_ROWS_PER_PAGE,
  SET_ORDER,
  SET_ORDERBY,
  SEARCH,
  INITIALIZE_SUCCESS,
  SET_CLIENT_SUCCESS,
  SET_ORDER_TYPE_SUCCESS,
  SET_SHIPPING_TYPE_SUCCESS,
  GET_SHIPPING_SUCCESS,
  SET_SUBSIDIARY_SUCCESS,
  SET_SUBSIDIARY_STOCK_SUCCESS,
  SET_PAYMENT_OPTION_SUCCESS,
  SET_PAYMENT_PLAN_SUCCESS,
  SET_PAYMENT_PLAN_GROUP_SUCCESS,
  SET_PAYMENT_PLAN_GROUP_ETHICAL,
  SET_PAYMENT_PLAN_GROUP_GENERIC,
  SET_LOAD_TYPE_SUCCESS,
  UPDATE_ORDER,
  UPDATE_SELECTS,
  SET_SHOW_MODAL_PRODUCT_DETAIL,
  SET_HIDE_CART_RECOMMENDATIONS,
  SET_SHOW_MODAL_PRODUCT_VARIANT,
  ADD_PRODUCT_CART_SUCCESS,
  REMOVE_PRODUCT_CART_SUCCESS,
  CLEAR_ALL_CART,
  CLEAR_ORDER_ITEM,
  LOAD_PRODUCT_SUCCESS,
  UPDATE_PRODUCT_SUCCESS,
  SET_SHOW_MODAL_ORDER,
  UPDATE_MODAL_ORDER,
  SET_DELIVERY_ADDRESS_CODE,
  SET_ESTIMATED_DELIVERY_DATE,
  SET_ORDER_NUMBER,
  SET_SOME_OBSERVATION,
  SET_DEFAULT_DELIVERY,
  SET_BUDGET,
  SET_SEND_PDF_INFO,
  VALIDATE_ORDER_SUCCESS,
  STORE_PERCENTUAL_RATE,
  STORE_ORDER_INFOS,
  GET_ALL_FILTER_SELECTS_SUCCESS,
  SET_SHOW_MODAL_REASON,
  SET_SHOW_MODAL_PRODUCT_PROMOTION_KIT,
  SET_BILLING_DATE,
  GET_PROMOTION_KIT_LIST_SUCESS,
  SET_BONIFICATION_BOUND,
  SET_OPEN_MODAL_BONIFICATION,
  SET_ORDER_BONIFICATION_SUCCESS,
  UPDATE_ORDER_MARGIN,
  GET_ALTERNATIVE_PRODUCT_SUCCESS,
  SET_LOADING_VINCULATED_ORDER,
  SPLIT_ORDER_VALIDATION_SUCCESS,
  SET_SHIPPING_DELIVERY_PROVIDER,
  SET_SHIPPING_FEE_REQUEST,
  SET_VENDOR,
  SET_ASSISTED_SALE_TYPE,
  APPLY_DISCOUNT_TO_ALL_ITEMS_SUCCESS,
  UPDATE_BALANCE_SUCCESS,
  CLEAR_ORDER,
  SET_SUBSIDIARY_PRODUCT_STOCK_SUCCESS,
  SET_SUBSIDIARY_STOCK_RESTRICTIONS,
  UPDATE_ORDER_OTHER_EXPENSES,
  SET_BONIFICATION_TYPE_REQUEST
} from './order-constants';
import { marginControl, totalizers, updateTotalizers } from './order-utils';

export interface OrderState {
  loadingData: Boolean;
  loadingProductData: Boolean;
  loadingSend: Boolean;
  loadingSelects: Boolean;
  loadingVinculatedOrder: Boolean;
  form: {
    data: OrderCreateInterface;
    selects: any;
    selectsDefault: any;
    itemOrder?: OrderItemCreateInterface | null;
    showModalProductDetail?: ModalType<ProductModalProps>;
    // Vale só para o pedido atual; resetado em INITIALIZE_SUCCESS e CLEAR_ORDER
    hideCartRecommendations?: boolean;
    showModalProductVariant?: ModalType<ProductModalProps>;
    showModalBlockReason?: ModalType<ReasonInterface>;
    showModalPromotionKit?: ModalType<PromotionInterface>;
    showModalPromotionKitList?: {
      data: PromotionInterface[];
      skip: number;
      limit: number;
      hasMore: boolean;
      show: boolean;
    };
    subsidiaryStockRestrictions?: SubsidiaryStockRestrictionsInterface | null;
    showOrderModal?: any;
    errors: any[];
    validate: any;
    showBonificationModal?: ModalType<any>;
    orderItem?: any;
    splitOrderValidation?: any;
  };
  list: StateListType<OrderInterface>;
}

const INITIAL_STATE: OrderState = {
  loadingData: true,
  loadingProductData: false,
  loadingSend: false,
  loadingSelects: false,
  loadingVinculatedOrder: false,
  form: {
    data: {
      orderDetails: {
        orderType: 'normal',
        deliveryExternalCode: null,
        shippingType: 'free',
        shippingFee: 0,
        subsidiary: null,
        subsidiaryStock: null,
        paymentOption: null,
        paymentPlan: null,
        percentualRate: null,
        creationDate: new Date(),
        otherExpenses: 0,
        bonificationTypeValue: null
      },
      complementary: false,
      estimatedDeliveryDate: {
        date: '',
        default: new Date().toISOString(),
        hasChanged: false
      },
      orderType: null,
      delivery: null,
      fromBudget: false,
      shipping: {
        shippingValue: 0,
        days: 0,
        minimumValue: 0,
        freteNotFound: true
      },
      sendOrderInfo: {
        isSend: false,
        email: '',
        cellphoneNumber: ''
      },
      userExternalCode: '',
      clientExternalCode: '',
      orderStatus: '',
      orderItems: [],
      totalPrice: 0,
      totalQuantity: 0,
      totalsMargin: {},
      balance: null
    },
    showOrderModal: {
      show: false,
      type: 'loading',
      data: {
        status: '',
        orderNumber: '',
        orderID: '',
        message: '',
        link: ''
      }
    },
    validate: null,
    itemOrder: null,
    hideCartRecommendations: false,
    showModalProductDetail: {
      show: false,
      data: null
    },
    showModalProductVariant: {
      show: false,
      data: null
    },
    showModalBlockReason: {
      show: false,
      data: null
    },
    showModalPromotionKit: {
      show: false,
      data: null
    },
    showModalPromotionKitList: {
      data: null,
      skip: 0,
      limit: 10,
      hasMore: true,
      show: false
    },
    subsidiaryStockRestrictions: null,
    showBonificationModal: {
      show: false
    },
    errors: [],
    selects: null,
    selectsDefault: null,
    splitOrderValidation: null
  },
  list: {
    hasRemotePagination: true,
    allRows: [],
    rows: [],
    rowsCount: 0,
    page: 0,
    rowsPerPage: 10,
    rowsPerPageOptions: [10, 25, 50, 100],
    selected: [],
    selectedCount: 0,
    order: 'desc',
    orderBy: 'updatedAt',
    total: {},
    selects: {
      clients: [],
      filiais: []
    }
  }
};

const usersReducer = (state: OrderState = { ...INITIAL_STATE }, action: IReduxAction) => {
  switch (action.type) {
    case SET_LOADING_PRODUCT_DATA: {
      return {
        ...state,
        loadingProductData: action.loading
      };
    }

    case SET_LOADING_DATA: {
      return {
        ...state,
        loadingData: action.loading
      };
    }

    case SET_LOADING_SEND: {
      return {
        ...state,
        loadingSend: action.loading
      };
    }

    case SET_LOADING_SELECTS: {
      return {
        ...state,
        loadingSelects: action.loading
      };
    }

    case SET_LOADING_VINCULATED_ORDER: {
      return {
        ...state,
        loadingVinculatedOrder: action.loading
      };
    }

    case GET_ALL_SUCCESS: {
      return {
        ...state,
        list: {
          ...state.list,
          rows: [...action.payload.rows],
          rowsCount: action.payload.rowsCount,
          allRows: [...action.payload.rows],
          total: action.payload.total
        }
      };
    }

    case SEARCH: {
      const { queryText } = action;
      const results =
        queryText !== ''
          ? state.list.allRows.filter(
              (item: any) =>
                item?.names?.clientName?.toLowerCase().startsWith(queryText.toLowerCase()) ||
                item?.names?.clientName?.toLowerCase().includes(queryText.toLowerCase()) ||
                item?.clientExternalCode?.toString().startsWith(queryText) ||
                item?.names?.userName?.toLowerCase().startsWith(queryText.toLowerCase()) ||
                item?.names?.userName?.toLowerCase().includes(queryText.toLowerCase()) ||
                item?.userExternalCode?.toString().startsWith(queryText) ||
                item?.orderNumber?.toString().startsWith(queryText) ||
                item?.orderNumber?.toString().includes(queryText) ||
                item?.orderExternalNumber?.toString().startsWith(queryText) ||
                item?.orderExternalNumber?.toString().includes(queryText) ||
                item?.orderDetails?.referenceOriginalOrderNumber?.toString()?.startsWith(queryText) ||
                item?.orderDetails?.referenceOriginalOrderNumber?.toString()?.includes(queryText)
            )
          : state.list.allRows;

      return {
        ...state,
        list: {
          ...state.list,
          rows: [...results]
        }
      };
    }

    case GET_ALL_FILTER_SELECTS_SUCCESS: {
  return {
    ...state,
    list: {
      ...state.list,
      selects: {
        ...state.list.selects,
        clients: action.payload.clients,
        filiais: action.payload.filiais
      }
    }
  };
}

    case SET_OPEN_MODAL_BONIFICATION: {
      return {
        ...state,
        form: {
          ...state.form,
          showBonificationModal: {
            show: action.payload.show
          }
        }
      };
    }

    case SET_ORDER_BONIFICATION_SUCCESS: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            vinculatedOrder: action.payload?.vinculatedOrder,
            parentInternalCode: action.payload?.parentInternalCode,
            orderVinculatedExternalNumber: action.payload?.orderVinculatedExternalNumber
          }
        }
      };
    }

    case GET_BY_ID_SUCCESS: {
      return {
        ...state,
        form: {
          ...action.payload
        }
      };
    }

    case SET_PAGE: {
      return {
        ...state,
        list: {
          ...state.list,
          page: action.page
        }
      };
    }

    case SET_ROWS_PER_PAGE: {
      return {
        ...state,
        list: {
          ...state.list,
          rowsPerPage: action.rowsPerPage
        }
      };
    }

    case SET_ORDER: {
      return {
        ...state,
        list: {
          ...state.list,
          order: action.order
        }
      };
    }

    case SET_ORDERBY: {
      return {
        ...state,
        list: {
          ...state.list,
          page: 0,
          orderBy: action.orderBy
        }
      };
    }

    case INITIALIZE_SUCCESS: {
      return {
        ...state,
        form: {
          ...INITIAL_STATE.form,
          ...action.payload,
          data: {
            ...INITIAL_STATE.form.data,
            ...action.payload.data
          }
        }
      };
    }

    case UPDATE_ORDER: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            ...action.payload
          }
        }
      };
    }

    case UPDATE_SELECTS: {
      return {
        ...state,
        form: {
          ...state.form,
          selects: {
            ...state.form.selects,
            ...action.payload
          }
        }
      };
    }

    case SET_CLIENT_SUCCESS: {
      const { client, paymentPlan, paymentOption, paymentPlanGroup } = action.payload;
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            client,
            orderDetails: {
              ...state.form.data.orderDetails,
              paymentOption,
              paymentPlan,
              paymentPlanGroup
            }
          }
        }
      };
    }

    case SET_ORDER_TYPE_SUCCESS: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            orderDetails: {
              ...state.form.data.orderDetails,
              orderType: action.payload.orderType.type,
              subsidiary: action.payload.newSubsidiary
            },
            orderType: action.payload.orderType
          },
          selects: {
            ...state.form.selects,
            subsidiaries: action.payload.newSubsidiaries
          }
        }
      };
    }

    case SET_SHIPPING_TYPE_SUCCESS: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            orderDetails: {
              ...state.form.data.orderDetails,
              shippingType: action.payload
            }
          }
        }
      };
    }

    case SET_SHIPPING_FEE_REQUEST: {
      const itemsTotal = state.form.data.orderItems?.reduce(
        (acc, item) => acc + (item?.soldPrice ?? 0) * (item?.soldQuantity ?? 0) + (item?.tributacao?.values?.vlST ?? 0),
        0
      );
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            orderDetails: {
              ...state.form.data.orderDetails,
              shippingFee: action.shippingFee
            },
            shipping: {
              ...state.form.data.shipping,
              cost: action.shippingFee
            },
            totalPrice: itemsTotal + action.shippingFee
          }
        }
      };
    }

    case GET_SHIPPING_SUCCESS: {
      const { payload, precision } = action;

      const orderItems = produce(state?.form.data?.orderItems ?? [], draft => {
        const itemIndex = draft.findIndex(i => i.cartId === payload.cartId);
        if (itemIndex >= 0) {
          draft[itemIndex] = payload;
        }
      });

      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            ...updateTotalizers({ orderItems, orderDetails: state.form.data.orderDetails, precision }),
            totalMarginPercentage: payload?.totalMarginPercentage,
            totalMarginValue: payload?.totalMarginValue,
            shipping: payload
          }
        }
      };
    }

    case SET_SUBSIDIARY_SUCCESS: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            orderDetails: {
              ...state.form.data.orderDetails,
              subsidiary: action.payload
            }
          }
        }
      };
    }

    case SET_SUBSIDIARY_STOCK_SUCCESS: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            orderDetails: {
              ...state.form.data.orderDetails,
              subsidiaryStock: action.payload
            }
          }
        }
      };
    }

    case SET_SUBSIDIARY_PRODUCT_STOCK_SUCCESS: {
      return {
        ...state,
        form: {
          ...state.form,
          orderItem: {
            ...state.form.orderItem,
            subsidiaryStock: action.payload
          }
        }
      };
    }

    case SET_PAYMENT_OPTION_SUCCESS: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            orderDetails: {
              ...state.form.data.orderDetails,
              paymentOption: action.payload
            }
          }
        }
      };
    }

    case SET_PAYMENT_PLAN_SUCCESS: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            orderDetails: {
              ...state.form.data.orderDetails,
              paymentPlan: action.payload
            }
          }
        }
      };
    }

    case SET_PAYMENT_PLAN_GROUP_SUCCESS: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            orderDetails: {
              ...state.form.data.orderDetails,
              paymentPlanGroup: action.payload
            }
          }
        }
      };
    }

    case SET_PAYMENT_PLAN_GROUP_ETHICAL: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            orderDetails: {
              ...state.form.data.orderDetails,
              paymentPlanGroup: {
                ...state.form.data.orderDetails.paymentPlanGroup,
                paymentPlan1: action.payload
              }
            }
          }
        }
      };
    }

    case SET_PAYMENT_PLAN_GROUP_GENERIC: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            orderDetails: {
              ...state.form.data.orderDetails,
              paymentPlanGroup: {
                ...state.form.data.orderDetails.paymentPlanGroup,
                paymentPlan2: action.payload
              }
            }
          }
        }
      };
    }

    case SET_BONIFICATION_TYPE_REQUEST: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            orderDetails: {
              ...state.form.data.orderDetails,
              bonificationTypeValue: action.payload
            }
          }
        }
      };
    }

    case SET_LOAD_TYPE_SUCCESS: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            load: {
              loadType: action.payload
            }
          }
        }
      };
    }

    case SET_SHOW_MODAL_PRODUCT_VARIANT: {
      return {
        ...state,
        form: {
          ...state.form,
          showModalProductVariant: {
            ...action.payload
          }
        }
      };
    }

    case SET_HIDE_CART_RECOMMENDATIONS: {
      return {
        ...state,
        form: {
          ...state.form,
          hideCartRecommendations: action.payload
        }
      };
    }

    case SET_SHOW_MODAL_PRODUCT_DETAIL: {
      return {
        ...state,
        form: {
          ...state.form,
          showModalProductDetail: {
            ...action.payload
          }
        }
      };
    }

    case SET_SHOW_MODAL_ORDER: {
      return {
        ...state,
        form: {
          ...state.form,
          showOrderModal: {
            show: action.show,
            type: 'loading'
          }
        }
      };
    }

    case UPDATE_MODAL_ORDER:
      return {
        ...state,
        form: {
          ...state.form,
          showOrderModal: {
            ...state.form.showOrderModal,
            ...action.payload
          }
        }
      };

    case ADD_PRODUCT_CART_SUCCESS: {
      const orderItem = action.payload;

      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            totalQuantity: state?.form?.data?.totalQuantity
              ? Number(state?.form?.data?.totalQuantity) + Number(orderItem?.soldQuantity)
              : Number(orderItem?.soldQuantity),
            totalPrice: state?.form?.data?.totalPrice
              ? state?.form?.data?.totalPrice + orderItem?.totalPrice
              : orderItem?.totalPrice,
            orderItems: state?.form?.data?.orderItems ? [...state?.form?.data?.orderItems, orderItem] : [orderItem]
          }
        }
      };
    }

    case REMOVE_PRODUCT_CART_SUCCESS: {
      const { productId, productGrade, tenantID, configuration } = action;
      const newItems = [...state?.form?.data?.orderItems.filter(item => item.cartId !== productId)];

      const _totalizers = totalizers({
        orderItems: newItems,
        precision: configuration?.decimals,
        tenantId: tenantID
      });

      const marginControlItems = marginControl({
        financialCostTotal: _totalizers.financialCostTotal,
        productsTotal: _totalizers.productsTotal,
        productsTotalBonificated: _totalizers.productsTotalBonificated,
        configuration
      });

      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            totalQuantity: state?.form?.data?.totalQuantity
              ? Number(state?.form?.data?.totalQuantity) - Number(productGrade?.soldQuantity)
              : Number(productGrade?.soldQuantity),
            totalPrice: state?.form?.data?.totalPrice
              ? state?.form?.data?.totalPrice - productGrade?.totalPrice
              : productGrade?.totalPrice,
            orderItems: state?.form?.data?.orderItems.length > 0 ? [...newItems] : null,
            ...updateTotalizers({
              orderItems: newItems,
              orderDetails: state.form.data.orderDetails,
              precision: configuration?.decimals,
              tenantId: tenantID
            }),
            totalsMargin: {
              ...marginControlItems
            }
          }
        }
      };
    }

    case CLEAR_ALL_CART: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            orderItems: []
          }
        }
      };
    }

    case CLEAR_ORDER_ITEM: {
      return {
        ...state,
        form: {
          ...state.form,
          orderItem: null
        }
      };
    }

    case CLEAR_ORDER: {
      return {
        ...state,
        form: {
          ...state.form,
          hideCartRecommendations: false,
          data: {
            ...INITIAL_STATE.form.data,
            orderDetails: {
              ...INITIAL_STATE.form.data.orderDetails,
              creationDate: new Date()
            },
            estimatedDeliveryDate: {
              ...INITIAL_STATE.form.data.estimatedDeliveryDate,
              default: new Date().toISOString()
            }
          }
        }
      };
    }

    case LOAD_PRODUCT_SUCCESS: {
      const { payload } = action;

      return {
        ...state,
        form: {
          ...state.form,
          orderItem: {
            ...payload
          }
        }
      };
    }

    case UPDATE_PRODUCT_SUCCESS: {
      const { payload, tenantID, precision } = action;

      const orderItems = produce(state?.form.data?.orderItems ?? [], draft => {
        const itemIndex = draft.findIndex(i => i.cartId === payload.cartId);
        if (itemIndex >= 0) {
          draft[itemIndex] = payload;
        }
      });

      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            orderItems,
            ...updateTotalizers({
              orderItems,
              orderDetails: state.form.data.orderDetails,
              precision,
              tenantId: tenantID
            }),
            totalMarginPercentage: payload?.totalMarginPercentage,
            totalMarginValue: payload?.totalMarginValue
          }
        }
      };
    }

    case UPDATE_ORDER_MARGIN: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            totalsMargin: action?.payload
          }
        }
      };
    }

    case SET_SEND_PDF_INFO: {
      const { payload } = action;

      return {
        ...state,
        form: {
          ...state?.form,
          data: {
            ...state?.form?.data,
            sendOrderInfo: {
              ...state?.form?.data?.sendOrderInfo,
              ...payload
            }
          }
        }
      };
    }

    case SET_BONIFICATION_BOUND: {
      return {
        ...state,

        form: {
          ...state?.form,
          data: {
            ...state?.form?.data,
            orderDetails: {
              ...state?.form?.data?.orderDetails,
              bonificationApprovalSwitch: action.payload
            }
          }
        }
      };
    }

    case SET_DELIVERY_ADDRESS_CODE: {
      return {
        ...state,
        form: {
          ...state?.form,
          data: {
            ...state?.form?.data,
            orderDetails: {
              ...state?.form?.data?.orderDetails,
              deliveryAddressCode: action.payload
            }
          }
        }
      };
    }

    case SET_ESTIMATED_DELIVERY_DATE: {
      return {
        ...state,
        form: {
          ...state?.form,
          data: {
            ...state?.form?.data,
            estimatedDeliveryDate: action.payload
          }
        }
      };
    }

    case SET_BILLING_DATE: {
      return {
        ...state,
        form: {
          ...state?.form,
          data: {
            ...state?.form?.data,
            orderDetails: {
              ...state?.form?.data?.orderDetails,
              billingDate: action.payload
            }
          }
        }
      };
    }

    case SET_ORDER_NUMBER: {
      return {
        ...state,
        form: {
          ...state?.form,
          data: {
            ...state?.form?.data,
            orderDetails: {
              ...state?.form?.data?.orderDetails,
              clientOrderNumber: action.payload
            }
          }
        }
      };
    }

    case SET_SOME_OBSERVATION: {
      const { name, value } = action.payload;

      return {
        ...state,
        form: {
          ...state?.form,
          data: {
            ...state?.form?.data,
            orderDetails: {
              ...state?.form?.data?.orderDetails,
              [name]: value
            }
          }
        }
      };
    }

    case SET_DEFAULT_DELIVERY: {
      return {
        ...state,
        form: {
          ...state?.form,
          data: {
            ...state?.form?.data,
            delivery: action.payload,
            orderDetails: {
              ...state?.form?.data?.orderDetails,
              deliveryExternalCode: action?.payload?.externalCode
            }
          }
        }
      };
    }

    case SET_SHIPPING_DELIVERY_PROVIDER: {
      return {
        ...state,
        form: {
          ...state?.form,
          data: {
            ...state?.form?.data,
            orderDetails: {
              ...state?.form?.data?.orderDetails,
              providerDeliveryExternalCode: action?.payload
            }
          }
        }
      };
    }

    case SET_BUDGET: {
      const { payload: isBudget } = action;

      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 2);

      return {
        ...state,
        form: {
          ...state?.form,
          data: {
            ...state?.form?.data,
            budget: {
              budgetID: state?.form?.data?.budgetCopyID ?? Date.now(),
              effectedSale: !isBudget && state?.form?.data?.fromBudget,
              expirationDate,
              isBudget
            }
          }
        }
      };
    }

    case VALIDATE_ORDER_SUCCESS: {
      const { payload } = action;

      return {
        ...state,
        form: {
          ...state?.form,
          validate: {
            isBlock: payload.isBlock,
            messages: [...Object.entries(payload.messages).flat(Infinity)].filter((item, index) => index % 2 !== 0),
            notices: [...Object.entries(payload.notices).flat(Infinity)].filter((item, index) => index % 2 !== 0),
            types: payload.types
          }
        }
      };
    }

    case STORE_PERCENTUAL_RATE:
      return {
        ...state,
        form: {
          ...state?.form,
          data: {
            ...state?.form?.data,
            orderDetails: {
              ...state?.form?.data?.orderDetails,
              percentualRate: action.percentualRate
            }
          }
        }
      };

    case STORE_ORDER_INFOS:
      return {
        ...state,
        form: {
          ...state?.form,
          data: {
            ...state?.form?.data,
            orderDetails: {
              ...state?.form?.data?.orderDetails,
              ...action?.orderDetails
            }
          }
        }
      };

    case SET_VENDOR: {
      return {
        ...state,
        form: {
          ...state?.form,
          data: {
            ...state?.form?.data,
            orderDetails: {
              ...state?.form?.data?.orderDetails,
              vendor: action.vendor
            }
          }
        }
      };
    }

    case SET_ASSISTED_SALE_TYPE: {
      return {
        ...state,
        form: {
          ...state.form,
          orderItem: {
            ...state.form.orderItem,
            assistedSaleType: action.typeSale
          }
        }
      };
    }

    case SET_SHOW_MODAL_REASON: {
      return {
        ...state,
        form: {
          ...state.form,
          showModalBlockReason: {
            ...action.payload
          }
        }
      };
    }

    case SET_SHOW_MODAL_PRODUCT_PROMOTION_KIT: {
      return {
        ...state,
        form: {
          ...state.form,
          showModalPromotionKit: {
            ...action.payload
          }
        }
      };
    }

    case GET_PROMOTION_KIT_LIST_SUCESS: {
      return {
        ...state,
        form: {
          ...state.form,
          showModalPromotionKitList: {
            data: action?.payload?.data,
            skip: action?.payload?.skip,
            limit: action?.payload?.limit,
            hasMore: action?.payload?.hasMore,
            show: action?.payload?.show,
            count: action?.payload?.count
          }
        }
      };
    }

    case GET_ALTERNATIVE_PRODUCT_SUCCESS: {
      return {
        ...state,
        form: {
          ...state.form,
          orderItem: {
            ...state.form.orderItem,
            alternativeProducts: [...action.payload]
          }
        }
      };
    }

    case SPLIT_ORDER_VALIDATION_SUCCESS: {
      return {
        ...state,
        form: {
          ...state.form,
          splitOrderValidation: action.payload
        }
      };
    }

    case APPLY_DISCOUNT_TO_ALL_ITEMS_SUCCESS: {
      const { items, tenantID } = action.payload;
      const precision = action.precision;

      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form.data,
            orderItems: items,
            ...updateTotalizers({
              orderItems: items,
              orderDetails: state.form.data.orderDetails,
              precision,
              tenantId: tenantID
            })
          }
        }
      };
    }

    case UPDATE_BALANCE_SUCCESS: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state?.form?.data,
            balance: {
              difference: action.payload.difference,
              finalBalance: action.payload.finalBalance,
              itemDifference: action.payload.itemDifference
            }
          }
        }
      };
    }

    case SET_SUBSIDIARY_STOCK_RESTRICTIONS: {
      return {
        ...state,
        form: {
          ...state.form,
          subsidiaryStockRestrictions: action.payload
        }
      };
    }

    case UPDATE_ORDER_OTHER_EXPENSES: {
      return {
        ...state,
        form: {
          ...state.form,
          data: {
            ...state.form?.data,
            orderDetails: {
              ...state.form?.data.orderDetails,
              otherExpenses: action.payload
            }
          }
        }
      };
    }

    default:
      return state;
  }
};

export default usersReducer;
